import { handleActions, createAction } from "redux-actions";
import { createThunkActionCreator } from "utils/redux/createThunkActionCreator";
import * as thunkActionConstants from "utils/redux/thunkActionConstants";
import * as cardApi from "../services/api/card";
import { replace } from "react-router-redux";
import {
  prepperDataToDropDown,
  tableDataTransactions,
  tablePendingTransactions
} from "../utils/dataHelper";
import type {pendingTransactionsAuthorisationModel} from "../models/authorizationModel";
import type {ITransactions} from "../models/transactionMoel";
import {datadogRum} from "@datadog/browser-rum";

const reducerMap = {};

const RESET = "CARD/RESET";
export const reset = createAction(RESET);
reducerMap[RESET] = (state, action) => {
  return {
    ...action
  };
};

/**
 * @function fetch
 * @return {Function}
 */
export const FETCH = "CARD/FETCH";
export const fetch = createThunkActionCreator(FETCH, dispatch => {
  return new Promise(resolve => {
    cardApi
      .getCardAccount()
      .then( async cardData => {
          const isShowCardSection = cardData && cardData.accountId;
          if (location.pathname === "/") {
            // const redirectPath = cardData && cardData.accountId ? '/card' : '/installments';
            const redirectPath = isShowCardSection ? "/card/lobby" : "/installments";
            dispatch(replace(redirectPath));
          }
          if(isShowCardSection){
             const atpData = await cardApi.fetchAtpDetails();
             let defaultPaymentMethodData;
             try{
               defaultPaymentMethodData = await cardApi.getDefaultPaymentMethod();
             } catch (e) {
               datadogRum.addUserAction('failed fetching default payment method', {
                 event: {
                   error: e,
                 }
               });
             }
             resolve({
               ...cardData,
               atpDetails: atpData.customerAtp  || {},
               isAtpUpdateNeeded: atpData.isAtpUpdateNeeded,
               defaultPaymentMethod: defaultPaymentMethodData
             });
          }
          else{
            resolve(cardData);
          }
      })
      .catch(() => {
        dispatch(replace("/installments"));
      });
  });
});
reducerMap[FETCH] = (state, action) => {
  const { payload, ...rest } = action;
  if (action.status === thunkActionConstants.ACTION_STATUS_PENDING) {
    return { ...state, ...rest };
  }

  return { ...state, ...rest, card: { ...state.card, ...payload } };
};

/**
 * @function getStatement
 * @param {string} month
 * @return {Function}
 */
export const FETCH_STATEMENT = "CARD/FETCH_STATEMENT";
export const getStatement = createThunkActionCreator(
  FETCH_STATEMENT,
  statementDateKey => {
    return new Promise((resolve, reject) => {
      cardApi.getStatement(statementDateKey).then(statement => {
        if (statement && statement.statementDTO) {
          resolve(statement.statementDTO);
        }
        reject(statement);
      });
    });
  }
);
reducerMap[FETCH_STATEMENT] = (state, action) => {
  const { payload, ...rest } = action;
  if (action.status === thunkActionConstants.ACTION_STATUS_PENDING) {
    return { ...state, ...rest };
  }

  return {
    ...state,
    ...rest,
    statement: { ...state.statement, [payload.statementDate]: payload }
  };
};

export const FETCH_STATUS = "CARD/FETCH_STATUS";
export const getSunbitCard = createThunkActionCreator(
  FETCH_STATUS,
  () => {
    return new Promise((resolve, reject) => {
      cardApi.getNewData().then(newData => {
        if (newData) {
          resolve(newData);
        } else {
          reject();
        }
      }, (error) => {
        reject(error);
      });
    });
  }
);
reducerMap[FETCH_STATUS] = (state, action) => {
  const { payload, ...rest } = action;

  switch (rest.status) {
    case thunkActionConstants.ACTION_STATUS_PENDING:
      return {
        ...state,
        ...rest,
      };
    case thunkActionConstants.ACTION_STATUS_SUCCESS:
      return {
        ...state,
        ...rest,
        cardStatus: payload,
      };
    case thunkActionConstants.ACTION_STATUS_FAIL:
      return {
        ...state,
        ...action,
      };
  }
};

/**
 * @function getBalance
 * @return {Function}
 */
export const FETCH_BALANCE = "CARD/FETCH_BALANCE";
export const getBalance = createThunkActionCreator(FETCH_BALANCE, () => {
  return new Promise((resolve, reject) => {
    cardApi.getBalance().then(statement => {
      if (statement && statement.balanceDTO) {
        resolve(statement.balanceDTO);
      }
      reject(statement);
    });
  });
});
reducerMap[FETCH_BALANCE] = (state, action) => {
  const { payload, ...rest } = action;
  if (action.status === thunkActionConstants.ACTION_STATUS_PENDING) {
    return { ...state, ...rest };
  }

  return { ...state, ...rest, balance: payload };
};

interface ITransactionsRes {
  authorizations: pendingTransactionsAuthorisationModel,
  transactions: ITransactions
}

/**
 * @function getPendingAndCurrentTransactions
 * @return {Function}
 */
export const FETCH_PENDING_AND_CURRENT_TRANSACTIONS = 'CARD/FETCH_PENDING_AND_CURRENT_TRANSACTIONS';
export const getPendingAndCurrentTransactions = createThunkActionCreator(FETCH_PENDING_AND_CURRENT_TRANSACTIONS, async () => {
  return new Promise((resolve, reject) => {
    cardApi.getPendingAndCurrentTransactions().then((transactions: ITransactionsRes) => {
      if (transactions && transactions.transactions && transactions.authorizations) {
        resolve([transactions.authorizations, transactions.transactions]);
      } else {
        reject(transactions);
      }
    });
  });
});

reducerMap[FETCH_PENDING_AND_CURRENT_TRANSACTIONS] = (state, action) => {
  const { payload, ...rest } = action;
  if (action.status === thunkActionConstants.ACTION_STATUS_PENDING) {
    return { ...state, ...rest };
  }
  negatePayments(payload[0]);
  filterPaymentsFromTransactions(payload[0], payload[1]);
  return { ...state, ...rest, pendingTransactions: tablePendingTransactions(payload[0]), currentTransactions: tableDataTransactions(payload[1]) };
};


const negatePayments = (authorizations) => {
  for (let i=0; i < authorizations.length; i++) {
    let a = authorizations[i];
    if (a.transactionCode === "MI" && a.authorizationAmount > 0) {
      a.authorizationAmount = a.authorizationAmount * -1;
    }
  }
};

// remove every authorization that is of type "MI" (Payment) which already appears on the posted transactions
const filterPaymentsFromTransactions = (authorizations, postedTransactions) => {
  for (let i = authorizations.length -1 ; i >= 0; i--) {
    let a = authorizations[i];
    if (a.transactionCode === "MI") {
      let z = postedTransactions.find(postedT => {
        return (a.authorizationAmount === postedT.aggregateTransactionAmount)
          && (a.authorizationDate === postedT.postDate);});
      if (z) {
        authorizations.splice(i, 1);
      }
    }
  }
};


/**
 * @function getTransactionsByDate
 * @return {Function}
 */
export const FETCH_TRANSACTIONS_BY_DATE = "CARD/FETCH_TRANSACTIONS_BY_DATE";
export const getTransactionsByDate = createThunkActionCreator(
  FETCH_TRANSACTIONS_BY_DATE,
  (data) => {
    return new Promise((resolve, reject) => {
      cardApi.getTransactionsByDate(data).then(transactionsByDate => {
        if (transactionsByDate) {
          resolve(transactionsByDate);
        } else {
          reject(transactionsByDate);
        }
      }, (error) => {
        reject(error);
      });
    });
  }
);
reducerMap[FETCH_TRANSACTIONS_BY_DATE] = (state, action) => {
  const { payload, ...rest } = action;
  if (action.status === thunkActionConstants.ACTION_STATUS_PENDING) {
    return { ...state, ...rest , transactionsByDate: {statement : {},  statementTransactions: [], sumTransactionAmount: undefined, supplementalDoc: undefined}};
  }
  else if (action.status === thunkActionConstants.ACTION_STATUS_FAIL)  {
    return {...state, ...action, transactionsByDate: {statement : {},  statementTransactions: [], sumTransactionAmount: undefined, supplementalDoc: undefined}};
  }
  return {
    ...state,
    ...rest,
    transactionsByDate: {
      statement: payload.statement,
      statementTransactions: tableDataTransactions(payload.transactionsWithSummary.transactions),
      sumTransactionAmount: payload.transactionsWithSummary.sumTransactionAmount,
      supplementalDoc: payload.supplementalDoc // {supDocFileExist: boolean}, can be null
    }
  };
};

export const CARD_MAKE_PAYMENT = "CARD/MAKE_PAYMENT";
export const cardMakePayment = createThunkActionCreator(
  CARD_MAKE_PAYMENT,
  paymentDetails => {
    return cardApi.makePayment(paymentDetails).then(response => response.data);
  }
);

reducerMap[CARD_MAKE_PAYMENT] = (state, action) => {
  const { payload, ...rest } = action;
  switch (rest.status) {
    case thunkActionConstants.ACTION_STATUS_SUCCESS:
      return { ...state, ...rest, payment: { ...state.payment, ...payload } };

    case thunkActionConstants.ACTION_STATUS_PENDING:
      return { ...state, ...action, payment: {} };

    case thunkActionConstants.ACTION_STATUS_FAIL:
      return {
        ...state, ...action, payment: { ...state.payment, ...rest, error: action.error }
      };
  }
};

/**
 * @function setAutoPay
 * @return {Function}
 */
export const CARD_SET_AUTOPAY = 'CARD/SET_AUTOPAY';
export const setAutoPay = createThunkActionCreator(CARD_SET_AUTOPAY, async (isEnabled, peymentMethodDetails) => {
  if (peymentMethodDetails) {
    await cardApi.addPaymentMethod(peymentMethodDetails);
  }
  return cardApi.setAutoPay(isEnabled);
});



reducerMap[CARD_SET_AUTOPAY] = (state, action) => {
  const { payload, ...rest } = action;
  return { ...state, ...rest, card: { ...state.card, autoPaySetting: payload } };
};


/**
 * @function setIncomeAndExpenses
 * @returns {Function}
 */

export const CARD_UPDATE_INCOME_EXPENSES = 'CARD/CARD_UPDATE_INCOME_EXPENSES';
export const setIncomeAndExpenses = createThunkActionCreator(CARD_UPDATE_INCOME_EXPENSES, async(atpDetails = {}) => {
  return cardApi.updateIncomeAndHousing(atpDetails);
});

reducerMap[CARD_UPDATE_INCOME_EXPENSES] = (state, action) => {
  const { payload, ...rest } = action;
  return { ...state, ...rest, card: { ...state.card, atpDetails: payload ,isAtpUpdateNeeded: false} };
};

/**
 * @function setIncomeAndExpenses
 * @returns {Function}
 */

export const CARD_FETCH_INCOME_EXPENSES = 'CARD/CARD_FETCH_INCOME_EXPENSES';
export const fetchAtpDetails = createThunkActionCreator(CARD_FETCH_INCOME_EXPENSES, async() => {
  return cardApi.fetchAtpDetails();
});

reducerMap[CARD_FETCH_INCOME_EXPENSES] = (state, action) => {
  const { payload, ...rest } = action;
  return { ...state, ...rest, card: { ...state.card, atpDetails: payload.customerAtp, isAtpUpdateNeeded : payload.isAtpUpdateNeeded } };
};



/**
 * @function getDdTransactionsList
 * @return {Function}
 */
export const CARD_GET_DD_TRANSACTIONS = "CARD/GET_TRANSACTIONS_LIST";
export const getDdTransactionsList = createThunkActionCreator(
  CARD_GET_DD_TRANSACTIONS,
  () => {
  return new Promise((resolve, reject) => {
    cardApi.getDdTransactionsList().then(statement => {
      if (statement) {
        resolve(statement);
      }
      reject(statement);
    });
  });
});
reducerMap[CARD_GET_DD_TRANSACTIONS] = (state, action) => {
  const {payload, ...rest} = action;
  switch (rest.status) {
    case thunkActionConstants.ACTION_STATUS_SUCCESS:
      return {...state, ...rest, ddTransactionsList: prepperDataToDropDown(payload.dates, null , "Date")};

    case thunkActionConstants.ACTION_STATUS_PENDING:
      return {...state, ...action, ddTransactionsList: []};

    case thunkActionConstants.ACTION_STATUS_FAIL:
      return {
        ...state, ...action, ddTransactionsList: {...state.ddTransactionsList, ...rest, error: action.error}
      };
  }
      return {...state, ...rest, balance: payload};
};

export const reducer = handleActions(reducerMap, {
  card: {},
  statement: null,
  balance: {},
  transactions: [],
  payment: {},
  pendingTransactions: {},
});



// WEBPACK FOOTER //
// ./src/card/redux.js